const dictionaries = new Map<string, Map<string, string>>();
const loadingMap = new Map<string, Promise<void>>();

export async function loadDictionary(url: string = "/data/es.json"): Promise<void> {
  if (dictionaries.has(url)) return;
  if (loadingMap.has(url)) return loadingMap.get(url)!;

  const promise = (async () => {
    const res = await fetch(url);
    const data: Record<string, string> = await res.json();
    dictionaries.set(url, new Map(Object.entries(data)));
  })();

  loadingMap.set(url, promise);
  return promise;
}

export function loadDictionaryFromData(data: Record<string, string>, url: string = "/data/es.json"): void {
  dictionaries.set(url, new Map(Object.entries(data)));
}

let activeDictUrl: string = "/data/es.json";

export function setActiveDictionary(url: string): void {
  activeDictUrl = url;
}

function getDict(): Map<string, string> | undefined {
  return dictionaries.get(activeDictUrl);
}

// --- Strategies (mirroring simplemma's DefaultStrategy chain) ---
// Each returns a lemma or undefined if it can't handle the token.
// The chain: dictionaryLookup → hyphenRemoval → rules → prefixDecomposition → affixDecomposition
// Then fallback: toLowercase

/** Direct dictionary lookup with case-flip fallback. All languages. */
function dictionaryLookup(token: string): string | undefined {
  const dictionary = getDict();
  if (!dictionary || !token) return undefined;
  const result = dictionary.get(token);
  if (result) return result;
  // Case-flip: try lowercase if uppercase, capitalize if lowercase
  const flipped =
    token[0] === token[0]!.toUpperCase()
      ? token.toLowerCase()
      : token[0]!.toUpperCase() + token.slice(1);
  return dictionary.get(flipped);
}

/** Join hyphenated parts and look up; then try lemmatizing just the last part. All languages. */
function hyphenRemoval(token: string): string | undefined {
  const parts = token.split(/(-)/);
  if (parts.length <= 1 || !parts[parts.length - 1]) return undefined;

  // Try joining without hyphens
  const joined = parts.filter((p) => p !== "-").join("");
  if (!joined) return undefined;
  let candidate = joined.toLowerCase();
  if (token[0] === token[0]!.toUpperCase()) {
    candidate = candidate[0]!.toUpperCase() + candidate.slice(1);
  }
  const lemma = dictionaryLookup(candidate);
  if (lemma) return lemma;

  // Try looking up just the last part and replacing it
  const lastPart = parts[parts.length - 1]!;
  const lastLemma = dictionaryLookup(lastPart);
  if (lastLemma) return [...parts.slice(0, -1), lastLemma].join("");

  return undefined;
}

const EN_IES_ENDINGS = ["cies", "ries", "ties", "qies"] as const;
const EN_S_ENDINGS = ["doms", "isms", "ists", "ments", "nces", "ships", "tions", "ums"] as const;

/** Rule-based English morphology fallback (ported from simplemma). */
function rulesEn(token: string): string | undefined {
  const t = token.toLowerCase();
  if (t.length > 7 && EN_IES_ENDINGS.some(e => t.endsWith(e))) return t.slice(0, -3) + "y";
  if (EN_S_ENDINGS.some(e => t.endsWith(e))) return t.slice(0, -1);
  if (t.endsWith("esses")) return t.slice(0, -2);
  if (t.endsWith("trices")) return t.slice(0, -3) + "x";
  return undefined;
}

/** Fallback: return lowercased token. */
function toLowercase(token: string): string {
  return token.toLowerCase();
}

// --- Public API ---

export function lemmatize(word: string, lang?: string): string {
  if (!getDict()) return word;
  return (
    dictionaryLookup(word) ??
    hyphenRemoval(word) ??
    (lang === "en" ? rulesEn(word) : undefined) ??
    toLowercase(word)
  );
}

export function tokenize(text: string): string[] {
  return text.match(/[\p{L}\p{M}]+(?:-[\p{L}\p{M}]+)*/gu) ?? [];
}

export function lemmatizeText(
  text: string,
  lang?: string,
): Array<{ word: string; lemma: string }> {
  const tokens = tokenize(text);
  return tokens.map((word) => ({
    word,
    lemma: lemmatize(word, lang),
  }));
}

export function isLoaded(url?: string): boolean {
  return dictionaries.has(url ?? activeDictUrl);
}
